import type { ApiResource } from "../../shared/api/resource.js";

export type BacktestStatus = "cancelled" | "completed" | "failed" | "queued" | "running";

export interface BacktestView {
  readonly artifactDownloadUrl?: string;
  readonly artifactKey?: string;
  readonly botId: string;
  readonly botName?: string;
  readonly botRunId: string;
  readonly completedAt?: string;
  readonly createdAt: string;
  readonly failure?: string;
  readonly id: string;
  readonly progressPercent: number;
  readonly request: {
    readonly adjustment: string;
    readonly commissionPerShareUsd: string;
    readonly minimumCommissionUsd?: string;
    readonly fillTiming: string;
    readonly from: string;
    readonly initialCashUsd: string;
    readonly interval: string;
    readonly slippageBasisPoints: number;
    readonly symbol: string;
    readonly to: string;
  };
  readonly revisionId: string;
  readonly revisionNumber: number;
  readonly status: BacktestStatus;
  readonly summary?: {
    readonly barCount: number;
    readonly fillCount: number;
    readonly finalEquityUsd: string;
    readonly initialCashUsd: string;
    readonly realizedPnlUsd: string;
    readonly totalFeesUsd: string;
  };
  readonly updatedAt: string;
  readonly versions: {
    readonly engineVersion: string;
    readonly marketDataVersion: string;
    readonly strategyRuntimeVersion: string;
  };
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function status(value: unknown): BacktestStatus {
  return ["cancelled", "completed", "failed", "queued", "running"].includes(String(value))
    ? (value as BacktestStatus)
    : "failed";
}

export function backtestView(resource: ApiResource): BacktestView {
  const request = record(resource.request);
  const revision = record(resource.revision);
  const versions = record(resource.versions);
  const summary = record(resource.summary);
  const artifact = record(resource.artifact);
  const downloadUrl = text(artifact.downloadUrl) || text(artifact.url);
  const completedAt = text(resource.completedAt);
  const failure = text(resource.failure);
  const botName = text(resource.botName);
  return {
    ...(downloadUrl === "" ? {} : { artifactDownloadUrl: downloadUrl }),
    ...(text(artifact.key) === "" ? {} : { artifactKey: text(artifact.key) }),
    botId: text(resource.botId) || text(revision.botId),
    ...(botName === "" ? {} : { botName }),
    botRunId: text(resource.botRunId),
    ...(completedAt === "" ? {} : { completedAt }),
    createdAt: text(resource.createdAt),
    ...(failure === "" ? {} : { failure }),
    id: resource.id,
    progressPercent: Math.min(100, Math.max(0, numberValue(resource.progressBasisPoints) / 100)),
    request: {
      adjustment: text(request.adjustment),
      commissionPerShareUsd: text(request.commissionPerShareUsd),
      ...(text(request.minimumCommissionUsd) === ""
        ? {}
        : { minimumCommissionUsd: text(request.minimumCommissionUsd) }),
      fillTiming: text(request.fillTiming),
      from: text(request.from),
      initialCashUsd: text(request.initialCashUsd),
      interval: text(request.interval),
      slippageBasisPoints: numberValue(request.slippageBasisPoints),
      symbol: text(request.symbol) || text(revision.symbol),
      to: text(request.to),
    },
    revisionId: text(revision.id),
    revisionNumber: numberValue(revision.revision),
    status: status(resource.status),
    ...(Object.keys(summary).length === 0
      ? {}
      : {
          summary: {
            barCount: numberValue(summary.barCount),
            fillCount: numberValue(summary.fillCount),
            finalEquityUsd: text(summary.finalEquityUsd),
            initialCashUsd: text(summary.initialCashUsd),
            realizedPnlUsd: text(summary.realizedPnlUsd),
            totalFeesUsd: text(summary.totalFeesUsd),
          },
        }),
    updatedAt: text(resource.updatedAt),
    versions: {
      engineVersion: text(versions.engineVersion),
      marketDataVersion: text(versions.marketDataVersion),
      strategyRuntimeVersion: text(versions.strategyRuntimeVersion),
    },
  };
}
